import React from 'react';
import { Link } from 'react-router-dom';

const CaseStudies = () => {
  const feedback = [
    {
      quote: "Avenir pushed me to think differently – it surfaced data points I wouldn't have normally thought to include, which helped me get a better grasp of the data instead of leaning on the same facts I typically fell back on. It also took a lot of the heavy lifting out of the recommendations process by generating new ones and tying them directly to the financial impact, which made the whole summary come together a lot more cohesively.",
      role: 'Data Analyst',
    },
    {
      quote: "The big improvement I've noticed with Avenir is how quickly and accurately it can take our Plan Performance Review and find the key topics to get it down to the executive summary. This has improved the amount of time I spend working on these reports, allowing me to spend more time on my other projects and reports.",
      role: 'Financial Associate',
    },
    {
      quote: "It increased my efficiency by letting me verify points rather than find them on my own, and by suggesting solutions for focus areas.",
      role: 'Data Analyst',
    },
    {
      quote: "I think Avenir is a fantastic tool that bridges the gap between data analysis and meaningful takeaways. The system does an amazing job at pulling the most important points from the Plan Performance Review to help create a client-ready executive summary. My favorite part is its ability to not only highlight what a group can do to better support member health, but also show how those changes can financially impact the group. This is definitely a tool I will be using for the foreseeable future.",
      role: 'Data Analyst',
    },
    {
      quote: "Avenir has helped to create efficiencies with our recommendations & financial impact by doing the 'thinking' for us. Instead of our team having to produce a recommendation on our own, Avenir now does that work for us and we can choose what makes the most sense for our clients. This also helps to create diversity among our recommendations for our clients.",
      role: 'Senior Financial Consultant',
    },
  ];

  const themes = [
    {
      title: 'Analysts can focus more on strategy',
      desc: "The firm's leadership sees Avenir as a way to free analysts from lower-value manual work and allow them to focus more on consulting and strategy. The firm has approached Avenir as a “junior analyst” that supports the team by taking on repetitive work and helping generate structured first drafts.",
    },
    {
      title: 'Avenir surfaces stronger insights',
      desc: 'Analysts reported that Avenir identified data points they may not have otherwise included. This helped them move beyond the same familiar facts and develop a broader, more complete understanding of the Plan Performance Review.',
    },
    {
      title: 'Recommendations are easier to produce',
      desc: "Several team members highlighted Avenir's ability to generate recommendations and tie them directly to financial impact. This reduced the manual burden of developing recommendations from scratch and helped the final summaries come together more cohesively.",
    },
    {
      title: 'Analysts verify instead of starting from zero',
      desc: 'Avenir changed the analyst workflow from “find every point manually” to “verify, refine, and select the best points.” This increased efficiency while keeping analyst expertise in the loop.',
    },
    {
      title: 'Outputs are more client-ready',
      desc: "The firm's analysts noted that Avenir helps bridge the gap between data analysis and meaningful takeaways. By pulling out important points from the Plan Performance Review and connecting them to practical actions, Avenir supports the creation of client-ready Executive Summaries, now with direct PowerPoint export for even faster client-ready outputs.",
    },
  ];

  return (
    <div className="page-content">
      {/* Hero */}
      <section className="hero">
        <div className="hero-content">
          <p className="hero-subtitle">Case Study</p>
          <h1 className="hero-title">
            National benefits advisory firm cuts financial reporting time by{' '}
            <span className="gradient-text">80%</span> with Avenir AI
          </h1>
          <p className="hero-description">
            A national benefits advisory firm partnered with Avenir to streamline its Executive
            Summary workflow, generating a first-draft summary in about 10 minutes and bringing
            analyst review down from roughly two hours to about 45 minutes after adopting
            Avenir's PowerPoint export.
          </p>
          <div className="cs-logos">
            <div className="cs-logo-avenir">
              <img src="/avenirlogowhite.png" alt="Avenir AI" />
            </div>
            <span className="cs-logo-x">&times;</span>
            <div className="cs-logo-tile">
              <span style={{ fontFamily: "'Avenir', 'Avenir Next', sans-serif", fontWeight: 700, fontSize: '0.95rem', letterSpacing: '0.03em', color: '#111' }}>
                National benefits advisory firm
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Executive Summary */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Executive Summary</p>
        <h2 className="cs-heading">The equivalent of one full employee, per deliverable</h2>
        <p>
          A national benefits advisory firm partnered with Avenir to streamline its Executive
          Summary workflow.
        </p>
        <p>
          Before Avenir, the firm's Executive Summary process required approximately 3 hours of
          analyst and peer review time per report. With Avenir, and after implementing
          PowerPoint formatting and export changes, the firm brought analyst review down from
          about two hours to roughly 45 minutes, saving close to two hours per Executive Summary.
        </p>
        <p>
          Avenir helps the firm's team upload the Plan Performance Review (PPR), select focus
          areas, and generate a first-draft Executive Summary in approximately 10 minutes.
          Analysts then review, refine, and finalize the summary, allowing them to spend less
          time manually identifying points and more time validating insights, selecting
          recommendations, and applying strategic judgment.
        </p>

        <div className="outcomes-grid" style={{ margin: '2rem 0' }}>
          <div className="outcome-card">
            <div className="outcome-value">~80%</div>
            <div className="outcome-label">Less financial reporting time</div>
            <p className="outcome-desc">Analyst reporting cut from hours to about 45 minutes.</p>
          </div>
          <div className="outcome-card">
            <div className="outcome-value">~45 min</div>
            <div className="outcome-label">Analyst review per summary</div>
            <p className="outcome-desc">Down from approximately two hours.</p>
          </div>
          <div className="outcome-card">
            <div className="outcome-value">~10 min</div>
            <div className="outcome-label">To a first-draft summary</div>
            <p className="outcome-desc">From upload to completed first draft.</p>
          </div>
        </div>

        <div className="cs-banner">
          <p className="cs-eyebrow">The Thesis</p>
          <p>
            "We took our executive summary review from two hours down to about 45 minutes. With
            the PowerPoint export changes, we're saving close to two hours on every report."
          </p>
        </div>
        <p style={{ color: '#747064', fontSize: '0.95rem' }}>
          — VP of Operations, National benefits advisory firm
        </p>
      </section>

      {/* About the firm */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">About the firm</p>
        <h2 className="cs-heading">Analytics built for the health care ecosystem</h2>
        <p>
          The firm is a national benefits advisory practice that partners with employee benefit
          agencies, equity firms, hospital systems, and other group purchasing platforms to
          deliver analytics, advisory support, and practical health care solutions.
        </p>
        <p>
          It combines analytics, health care expertise, and practical application to help agency
          partners deliver better recommendations and outcomes for their clients. With a
          foundation in employee benefits consulting, the firm understands how health care
          solutions interact with the full benefits ecosystem and how to translate complex
          information into actionable guidance.
        </p>
        <p>The firm's advisory platform is built around four core components:</p>
        <ul className="cs-keylist">
          <li>Health care ecosystem expertise</li>
          <li>Diverse client needs</li>
          <li>Computing power</li>
          <li>People</li>
        </ul>
      </section>

      {/* The Challenge */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">The Challenge</p>
        <h2 className="cs-heading">A highly manual, multi-touchpoint process</h2>
        <p>
          Executive Summaries are an important deliverable for the firm's agency partners and
          their clients. The firm's analysts produce approximately 110 Executive Summaries per
          year across seasonal batches: about 40 in May, 50 in February, and 20 off-cycle.
        </p>
        <p>
          These summaries translate complex Plan Performance Reviews into clear, client-ready
          insights and recommendations that benefits consultants can use with employers.
        </p>
        <p>
          Before Avenir, the Executive Summary process was highly manual. Analysts had to review
          the Plan Performance Review, identify the most relevant takeaways, draft the summary,
          refine the language, and prepare the output for review. After the analyst completed the
          initial review, the summary also required a separate peer review step.
        </p>
        <p>The original process took approximately:</p>
        <table className="cs-table">
          <thead>
            <tr>
              <th>Workflow Step</th>
              <th>Original Time</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Analyst Executive Summary review</td><td>2 hours</td></tr>
            <tr><td>Peer review</td><td>1 hour</td></tr>
            <tr><td>Total original review time</td><td>3 hours per Executive Summary</td></tr>
          </tbody>
        </table>
        <p>This created several operational challenges:</p>
        <ul className="cs-keylist">
          <li>Analysts spent significant time converting dense reports into client-ready summaries.</li>
          <li>Peer review added another hour to the production process.</li>
          <li>Executive Summary creation required multiple internal touchpoints.</li>
          <li>The process limited how quickly summaries could be produced at scale.</li>
          <li>Analyst time was spent on repetitive drafting and review instead of higher-value interpretation, recommendation development, and client strategy.</li>
        </ul>
      </section>

      {/* The Avenir Workflow */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">The Avenir Workflow</p>
        <h2 className="cs-heading">A faster, AI-assisted draft in about 10 minutes</h2>
        <p>
          With Avenir, the firm streamlined the Executive Summary process into a faster,
          AI-assisted workflow. The current workflow includes:
        </p>
        <table className="cs-table">
          <thead>
            <tr>
              <th>Avenir Step</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Upload PPR</td><td>2 minutes</td></tr>
            <tr><td>Select focus areas</td><td>1 minute 15 seconds</td></tr>
            <tr><td>Run Executive Summary</td><td>7 minutes</td></tr>
            <tr><td>Total Avenir generation time</td><td>10 minutes 15 seconds</td></tr>
          </tbody>
        </table>
        <p>
          After Avenir generates the Executive Summary draft, the firm's analysts review and
          refine the output. The analyst review process now takes approximately 45 minutes,
          compared with the previous two-hour analyst review, after the team implemented
          PowerPoint formatting and export changes.
        </p>
        <p>
          The previous peer review step, which took approximately 1 hour, has been largely
          eliminated, with only a small 10-minute calculation review remaining for the
          financials section.
        </p>
        <div className="cs-callout">
          <p>
            Instead of starting from a blank page, analysts can begin with a structured draft
            that identifies key topics, surfaces relevant data points, suggests recommendations,
            and connects those recommendations to financial impact.
          </p>
        </div>
      </section>

      {/* Results */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Results</p>
        <h2 className="cs-heading">From ~3 hours to under an hour of review</h2>
        <p>
          Using Avenir, the firm reduced total review time from about 3 hours to roughly 55
          minutes per Executive Summary.
        </p>
        <table className="cs-table">
          <thead>
            <tr>
              <th>Workflow Step</th>
              <th>Before Avenir</th>
              <th className="cs-after">With Avenir</th>
              <th className="cs-after">Time Saved</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Analyst Executive Summary review</td><td>2 hours</td><td>45 minutes</td><td>1 hour and 15 minutes</td></tr>
            <tr><td>Peer review / financial review</td><td>1 hour</td><td>10 minutes</td><td>50 minutes</td></tr>
            <tr><td>Total review time</td><td>3 hours</td><td>55 minutes</td><td>2 hours 5 minutes</td></tr>
          </tbody>
        </table>
        <p>
          Accounting for the broader workflow improvements, including the PowerPoint formatting
          and export changes, the firm saves close to two hours per Executive Summary. The
          Avenir generation process itself takes about 10 minutes from upload to completed first
          draft.
        </p>

        <h3 style={{ fontFamily: "'Avenir', sans-serif", fontSize: '1.25rem', marginTop: '2rem', marginBottom: '0.75rem', color: '#111' }}>
          Key Outcomes
        </h3>
        <ul className="cs-keylist">
          <li>Analyst review reduced from about two hours to roughly 45 minutes</li>
          <li>Roughly 80% less financial reporting time end to end</li>
          <li>Close to two hours saved per Executive Summary</li>
          <li>Peer review largely eliminated, reducing the number of analysts needed per review from two to one</li>
          <li>Executive Summary draft generated in approximately 10 minutes</li>
          <li>Direct PowerPoint export now streamlines the final formatting step</li>
        </ul>
      </section>

      {/* Operational Impact */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Operational Impact</p>
        <h2 className="cs-heading">Meaningful leverage across every summary</h2>
        <p>
          For the firm, the impact of Avenir goes beyond faster document generation. Avenir gives
          the team a stronger first draft, reduces repetitive review work, and allows analysts to
          spend more time on quality, interpretation, and client-facing insights.
        </p>
        <p>
          By reducing total review time from about 3 hours to under an hour per Executive
          Summary, the firm gains meaningful operational leverage across every summary produced.
          At scale, the savings become significant:
        </p>
        <table className="cs-table">
          <thead>
            <tr>
              <th>Executive Summary Volume</th>
              <th>Estimated Time Saved</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>20 off-cycle Executive Summaries</td><td>40 hours saved</td></tr>
            <tr><td>40 May Executive Summaries</td><td>80 hours saved</td></tr>
            <tr><td>50 February Executive Summaries</td><td>100 hours saved</td></tr>
            <tr><td>110 annual Executive Summaries</td><td>220 hours saved annually</td></tr>
          </tbody>
        </table>
        <p>
          For a benefits advisory team, this time can be redirected toward higher-value work such
          as client strategy, recommendation development, quality assurance, partner support, and
          additional reporting needs.
        </p>
        <p>
          Avenir also helps shift the analyst workflow from manual extraction to strategic
          verification. Instead of spending time finding every point from scratch, analysts can
          review Avenir's proposed insights, validate what matters most, and choose the
          recommendations that best fit the client.
        </p>
      </section>

      {/* Estimated Cost Savings */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Estimated Cost Savings</p>
        <h2 className="cs-heading">What the recovered time is worth</h2>
        <p>
          The firm provided an estimated analyst salary range of $55,000–$60,000 annually. Across a
          standard 2,080-hour work year, that is approximately $26–$29 per hour on salary alone, or
          roughly $36–$39 per hour once benefits, payroll taxes, and overhead are included (a
          standard fully-loaded multiplier of about 1.35). Avenir returns close to two hours per
          Executive Summary, which compounds across the firm's annual volume.
        </p>
        <table className="cs-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Estimate</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Analyst salary range</td><td>$55,000–$60,000 annually</td></tr>
            <tr><td>Salary-only hourly rate</td><td>~$26–$29/hour</td></tr>
            <tr><td>Fully-loaded hourly cost (≈1.35×)</td><td>~$36–$39/hour</td></tr>
            <tr><td>Time saved per Executive Summary</td><td>~2 hours</td></tr>
            <tr><td>Annual Executive Summary volume</td><td>~110 reports</td></tr>
            <tr><td>Analyst hours recovered per year</td><td>~220 hours</td></tr>
            <tr><td>Value of recovered time (salary only)</td><td>~$5,800–$6,350 per year</td></tr>
            <tr><td>Value of recovered time (fully loaded)</td><td>~$7,850–$8,560 per year</td></tr>
            <tr><td>Capacity recovered</td><td>~0.1 FTE (about 5–6 weeks of analyst time)</td></tr>
          </tbody>
        </table>
        <div className="cs-callout">
          <p>
            Two numbers tell two different stories. Per deliverable, the firm went from two
            reviewers to one, which is the "equivalent of one full employee per deliverable" the
            team describes. Per year, on the Executive Summary workflow alone, the recovered time
            equals roughly one-tenth of a full-time analyst, worth about $5,800 to $8,560 depending
            on whether you count salary or fully-loaded cost.
          </p>
        </div>
        <p>
          The path to a full analyst-equivalent is expansion. Applying the same workflow to the
          analyst's other recurring deliverables, such as monthly reports, renewal strategy briefs,
          and prospect intelligence reports, compounds the 220 hours toward a full-time-equivalent
          of recovered capacity. The figures above stay deliberately conservative: they exclude
          faster turnaround, improved recommendation quality, and the revenue value of redirecting
          analyst time toward client strategy.
        </p>
      </section>

      {/* Analyst Feedback */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Analyst Feedback</p>
        <h2 className="cs-heading">Faster and more strategic</h2>
        <p>
          The firm's team shared qualitative feedback showing that Avenir improved not only
          speed, but also the quality, depth, and strategic value of the Executive Summary
          process. Avenir helped analysts move from manual data extraction toward higher-value
          review, recommendation selection, and strategic interpretation. Several team members
          noted that the platform surfaced insights they may not have otherwise included,
          generated more diverse recommendations, and tied those recommendations directly to
          financial impact.
        </p>
        <div className="cs-quote-grid">
          {feedback.map((f, i) => (
            <div key={i} className="cs-quote">
              <p>&ldquo;{f.quote}&rdquo;</p>
              <span className="cs-author">
                {f.role}
              </span>
            </div>
          ))}
        </div>

        <h3 style={{ fontFamily: "'Avenir', sans-serif", fontSize: '1.25rem', marginTop: '2.5rem', marginBottom: '1rem', color: '#111' }}>
          Key Themes from the firm's feedback
        </h3>
        {themes.map((t, i) => (
          <div key={i} style={{ marginBottom: '1.25rem' }}>
            <p style={{ marginBottom: '0.35rem' }}>
              <strong style={{ color: '#258697' }}>{i + 1}. {t.title}</strong>
            </p>
            <p>{t.desc}</p>
          </div>
        ))}
      </section>

      {/* Why It Matters */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Why It Matters</p>
        <h2 className="cs-heading">Keeping human expertise in the loop</h2>
        <p>
          Executive Summaries are a critical bridge between analytics and advisory work. They help
          benefits consultants turn complex health care and plan performance information into
          clear recommendations for employers. However, creating these summaries manually can be
          time-intensive, especially when teams need to maintain quality, consistency, and
          client-ready language across multiple reports.
        </p>
        <p>
          Avenir helps the firm accelerate this process while keeping human expertise in the loop.
          Instead of replacing the analyst, Avenir supports the analyst by producing a structured
          draft that can be reviewed, refined, and finalized more efficiently. This allows the
          firm to preserve the consultative quality of its work while improving the speed and
          scalability of its internal operations.
        </p>
      </section>

      {/* What's Next */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">What's Next</p>
        <h2 className="cs-heading">Building on the efficiencies</h2>
        <p>
          The firm expects the Executive Summary process to become even more efficient as the
          workflow continues to improve. The team is continuing to build on these efficiencies in
          partnership with Avenir, and expects the time-savings numbers to improve further as the
          workflow becomes even more streamlined.
        </p>
        <p>Future opportunities may include expanding Avenir-supported workflows into:</p>
        <ul className="cs-keylist">
          <li>Condensed executive summaries</li>
          <li>Monthly reports</li>
          <li>Renewal strategy briefs</li>
          <li>Prospect intelligence reports</li>
          <li>Vendor comparison summaries</li>
          <li>Additional agency-support deliverables</li>
        </ul>
      </section>

      {/* Final Summary */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Final Summary</p>
        <h2 className="cs-heading">A faster, more scalable workflow</h2>
        <p>
          A national benefits advisory firm used Avenir to streamline its Executive Summary
          process, reducing total review time from about 3 hours to roughly 55 minutes per
          summary.
        </p>
        <p>
          By cutting analyst review from about two hours to roughly 45 minutes, largely
          eliminating a peer review step, and generating first drafts in about 10 minutes, Avenir
          helped the firm create a faster, more scalable workflow for producing client-ready
          summaries. Across approximately 110 Executive Summaries per year, that returns an
          estimated 220 hours of analyst capacity annually.
        </p>
        <div className="cs-banner">
          <p className="cs-eyebrow">The Payoff</p>
          <p>
            Analysts spend less time on repetitive drafting and review, and more time on the
            strategic, consultative work that drives better outcomes for agency partners and
            their clients.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="cta-wrapper">
          <div className="cta-glow"></div>
          <div className="cta-box">
            <h2 className="cta-title">See what Avenir can do for your team</h2>
            <p className="cta-desc">
              Turn dense reports into client-ready summaries in minutes, and give your analysts
              their time back.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-large">Book a Demo</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
